// Game library component for the library page

import React, { useCallback, useMemo, useState } from 'react';
import { CircleAlertIcon, LayoutGridIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router';
import type { GameType } from '@airhead-games/shared/types/configs/gameLibrary';
import SEO from '../../../seo/SEO';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '../../../components/ui/empty';
import { Skeleton } from '../../../components/ui/skeleton';
import {
  CollectionToolbar,
  FilterControls,
  SortControl,
} from '../../../components/collectionToolbar';
import GameLibraryCard from './GameLibraryCard';
import {
  DEFAULT_GAME_LIBRARY_SORT_DIRECTION,
  DEFAULT_GAME_LIBRARY_SORT_FIELD,
  GAME_LIBRARY_SORT_FIELDS,
  GAME_LIBRARY_SORT_OPTIONS,
  type GameLibrarySortField,
  sortGameLibraryGames,
} from './gameLibrarySort';
import { useRoomGameSelect, useRouteQueryState } from '../../../hooks';
import { useGames } from '../../../transport/cache';
import { analyticsService } from '../../../services/analyticsService';
import { SORT_DIRECTIONS, type SortDirection } from '../../../utilities/sort';

const PLACEHOLDER_CARDS = 6;

const GAME_LIBRARY_PATHNAME = '/library';

const GAME_TYPES: GameType[] = ['Free-For-All', 'Team', 'Cooperative'];

const slugifyGameType = (type: GameType) => type.toLowerCase();
const parseGameTypeSlug = (raw: string): GameType | undefined =>
  GAME_TYPES.find((type) => slugifyGameType(type) === raw);

const GameLibraryLoadingGrid: React.FC = () => (
  <div className="grid items-start gap-4 min-[1125px]:grid-cols-2 2xl:grid-cols-3">
    {Array.from({ length: PLACEHOLDER_CARDS }, (_, index) => (
      <Card key={index} className="gap-0 overflow-hidden p-0">
        <Skeleton className="aspect-[16/10] w-full rounded-none" />
        <div className="flex min-h-[3rem] items-center justify-center px-4 py-3 sm:min-h-[3.25rem]">
          <Skeleton className="h-5 w-2/3 rounded-md sm:h-6" />
        </div>
      </Card>
    ))}
  </div>
);

const GameLibrary: React.FC = () => {
  const {
    games,
    loading: isFetchingGames,
    error: fetchError,
    isFetched: catalogFetched,
    refetch: refetchCatalog,
  } = useGames();
  const { selectGame } = useRoomGameSelect();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { selected, values, updateRoute, updateEnums } = useRouteQueryState<
    { gameTypes: GameType[] },
    { sortField: GameLibrarySortField; sortDirection: SortDirection }
  >({
    pathname: GAME_LIBRARY_PATHNAME,
    searchParams,
    navigate,
    facets: {
      gameTypes: {
        key: 'type',
        parse: parseGameTypeSlug,
        serialize: slugifyGameType,
      },
    },
    enums: {
      sortField: {
        key: 'sort',
        values: GAME_LIBRARY_SORT_FIELDS,
        default: DEFAULT_GAME_LIBRARY_SORT_FIELD,
      },
      sortDirection: {
        key: 'dir',
        values: SORT_DIRECTIONS,
        default: DEFAULT_GAME_LIBRARY_SORT_DIRECTION,
      },
    },
  });

  const selectedGameTypes = selected.gameTypes;
  const { sortField, sortDirection } = values;

  const [openInfoGameId, setOpenInfoGameId] = useState<string | null>(null);
  const [revealedGameId, setRevealedGameId] = useState<string | null>(null);

  const visibleGames = useMemo(() => {
    if (isFetchingGames) {
      return [];
    }
    const filtered =
      selectedGameTypes.length === 0
        ? games
        : games.filter((game) =>
            selectedGameTypes.includes(game.gameWiki.gameType)
          );
    return sortGameLibraryGames(filtered, sortField, sortDirection);
  }, [games, isFetchingGames, selectedGameTypes, sortDirection, sortField]);

  const trackTypeFilterChange = useCallback(
    (gameType: GameType, action: 'add' | 'remove') => {
      analyticsService.trackButtonClick('Game Library Filter Type', {
        filter_value: gameType,
        filter_action: action,
        link_location: 'game_library_filter_panel',
      });
    },
    []
  );

  const setGameTypes = useCallback(
    (next: GameType[]) => {
      updateRoute({ gameTypes: next });
    },
    [updateRoute]
  );

  const toggleGameType = useCallback(
    (value: string) => {
      const type = value as GameType;
      const isSelected = selectedGameTypes.includes(type);
      trackTypeFilterChange(type, isSelected ? 'remove' : 'add');
      setGameTypes(
        isSelected
          ? selectedGameTypes.filter((entry) => entry !== type)
          : [...selectedGameTypes, type]
      );
    },
    [selectedGameTypes, setGameTypes, trackTypeFilterChange]
  );

  const setSortField = useCallback(
    (next: GameLibrarySortField) => {
      updateEnums({ sortField: next });
    },
    [updateEnums]
  );

  const setSortDirection = useCallback(
    (next: SortDirection) => {
      updateEnums({ sortDirection: next });
    },
    [updateEnums]
  );

  const activeFilterItems = useMemo(
    () =>
      selectedGameTypes.map((type) => ({
        id: `type:${type}`,
        label: type,
        onRemove: () => {
          trackTypeFilterChange(type, 'remove');
          setGameTypes(selectedGameTypes.filter((entry) => entry !== type));
        },
      })),
    [selectedGameTypes, setGameTypes, trackTypeFilterChange]
  );

  const hasCatalogGames = catalogFetched && games.length > 0;
  const hasActiveFilters = selectedGameTypes.length > 0;
  const hasVisibleGames = visibleGames.length > 0;

  return (
    <div className="flex min-h-full w-full flex-col bg-background text-foreground">
      <SEO />

      <main className="mx-auto flex w-full max-w-screen-2xl flex-1 flex-col gap-6 px-4 py-6 sm:px-6 lg:px-8">
        {fetchError ? (
          <Empty className="border bg-card">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <CircleAlertIcon />
              </EmptyMedia>
              <EmptyTitle>Oops! Something went wrong.</EmptyTitle>
              <EmptyDescription>{fetchError}</EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                type="button"
                onClick={() => {
                  void refetchCatalog();
                }}
              >
                Try again
              </Button>
            </EmptyContent>
          </Empty>
        ) : !catalogFetched || isFetchingGames ? (
          <GameLibraryLoadingGrid />
        ) : hasCatalogGames ? (
          <div className="flex flex-col gap-4">
            <CollectionToolbar
              filters={
                <FilterControls
                  groups={[
                    {
                      id: 'gameType',
                      label: 'Game Type',
                      options: GAME_TYPES.map((type) => ({
                        value: type,
                        label: type,
                      })),
                      selectedValues: selectedGameTypes,
                      onToggle: toggleGameType,
                      searchPlaceholder: 'Search game types',
                    },
                  ]}
                  activeItems={activeFilterItems}
                  onClearAll={() => setGameTypes([])}
                />
              }
              sort={
                <SortControl
                  options={GAME_LIBRARY_SORT_OPTIONS}
                  field={sortField}
                  direction={sortDirection}
                  onFieldChange={setSortField}
                  onDirectionChange={setSortDirection}
                />
              }
            />
            {hasVisibleGames ? (
              <div className="grid items-start gap-4 min-[1125px]:grid-cols-2 2xl:grid-cols-3">
                {visibleGames.map((game) => (
                  <GameLibraryCard
                    key={game.gameCore.gameId}
                    game={game}
                    infoOpen={openInfoGameId === game.gameCore.gameId}
                    revealed={
                      revealedGameId === game.gameCore.gameId ||
                      openInfoGameId === game.gameCore.gameId
                    }
                    onToggleInfo={() => {
                      setOpenInfoGameId((prev) =>
                        prev === game.gameCore.gameId
                          ? null
                          : game.gameCore.gameId
                      );
                    }}
                    onRevealEnter={() => {
                      setRevealedGameId(game.gameCore.gameId);
                      // Hovering a different card auto-closes info on any other card.
                      setOpenInfoGameId((prev) =>
                        prev && prev !== game.gameCore.gameId ? null : prev
                      );
                    }}
                    onRevealLeave={() => {
                      setRevealedGameId((prev) =>
                        prev === game.gameCore.gameId ? null : prev
                      );
                    }}
                    onStart={selectGame}
                  />
                ))}
              </div>
            ) : (
              <Empty className="border bg-muted/10 py-16">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <LayoutGridIcon aria-hidden="true" />
                  </EmptyMedia>
                  <EmptyTitle>No games match this filter</EmptyTitle>
                  <EmptyDescription>
                    Try removing a game type to widen your selection.
                  </EmptyDescription>
                </EmptyHeader>
                {hasActiveFilters ? (
                  <EmptyContent>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setGameTypes([])}
                    >
                      Clear all filters
                    </Button>
                  </EmptyContent>
                ) : null}
              </Empty>
            )}
          </div>
        ) : (
          <Card className="max-w-xl">
            <CardHeader className="gap-3">
              <CardTitle>No games loaded</CardTitle>
              <CardDescription>
                We could not show any games from the server. Check your
                connection and try again.
              </CardDescription>
              <Button
                type="button"
                className="w-fit"
                onClick={() => {
                  void refetchCatalog();
                }}
              >
                Try again
              </Button>
            </CardHeader>
          </Card>
        )}
      </main>
    </div>
  );
};

export default GameLibrary;
